import type { BuildingAnalyticEntity } from "building-types/buildingAnalyticEntityTypes";
import type { GeneratedCeiling } from "building-types/ceilings/ceiling";
import type { DoorUnit } from "building-types/doors/doorTypes";
import type { Driveway } from "building-types/driveways/drivewayTypes";
import type { FloorSystem } from "building-types/floors/floorSystemTypes";
import type {
  GeneratedFoundationHorizontalAssemblySubSystem,
  GeneratedFoundationSubSystem,
  GeneratedFoundationSuperSystem,
} from "building-types/foundations/foundationTypes";
import type { LumberRail } from "building-types/framing/lumberTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { Porch } from "building-types/porches/porch";
import type { GeneratedRoofSystem } from "building-types/roofs/generatedRoofSystemTypes";
import type { GeneratedRoofVolume } from "building-types/roofVolumes/roofVolumeTypes";
import type { EntityInstanceType } from "building-types/roomLayouts/roomLayoutEligibleDocEntity";
import type { Room } from "building-types/rooms/roomTypes";
import type { GeneratedRailing } from "building-types/settings/railingTypes";
import type { SkylightUnit } from "building-types/settings/roofTypes";
import type { GeneratedSite } from "building-types/site/site";
import type { GeneratedStairSystem } from "building-types/stairs/stairTypes";
import type { CornerBoards } from "building-types/trim/cornerBoardTypes";
import type { Wainscoting } from "building-types/wainscoting/wainscotingTypes";
import type { WallPlane, WallPlaneSubdomain } from "building-types/walls2/wallPlane";
import type { WallLayerType } from "building-types/walls/wallEnums";
import type { WallLayer } from "building-types/walls/wallTypes";
import type { WindowUnit } from "building-types/windows/windowTypes";
import type { BuildingEntityBase, DocBuildingEntityBase, DynamicBuildingEntityBase } from "./buildingEntityBase";
import type { BuildingWallPlaneHandle, BuildingEntityHandle } from "./buildingEntityHandle";
import type { BuildingEntityType } from "./buildingEntityTypeTypes";

export type BuildingEntity =
  | BuildingCeiling
  | CornerBoards
  | DoorUnit
  | Driveway
  | BuildingFloorSystem
  | BuildingLumber
  | BuildingFoundationHorizontalAssemblySubSystem
  | BuildingFoundationSubSystem
  | BuildingFoundationSuperSystem
  | Porch
  | BuildingSkylight
  | BuildingRailing
  | BuildingRoofSystem
  | BuildingRoom
  | BuildingSite
  | BuildingStairSystem
  | Wainscoting
  | WallPlane
  | BuildingWallPlaneSubdomain
  | WallLayer
  | WindowUnit
  | BuildingAnalyticEntity;

/**
 * @todo(Andrew): The following types need to have their base types satisfy
 * the [Doc]BuildingEntityBase interface; for instance, GeneratedCeiling should
 * extend DocBuildingEntityBase<"CEILING">. This will require a refactor of the
 * building generation process for each of these types.
 */
export type BuildingCeiling = GeneratedCeiling & DocBuildingEntityBase<"CEILING">;
export type BuildingFloorSystem = FloorSystem & BuildingEntityBase<"FLOOR_SYSTEM">;
export type BuildingLumber = LumberRail & DynamicBuildingEntityBase<"LUMBER">;
export type BuildingFoundationHorizontalAssemblySubSystem = GeneratedFoundationHorizontalAssemblySubSystem &
  BuildingEntityBase<"FOUNDATION_SYSTEM_HORIZONTAL_ASSEMBLY">;
export type BuildingFoundationSubSystem = GeneratedFoundationSubSystem & BuildingEntityBase<"FOUNDATION_SYSTEM">;
export type BuildingFoundationSuperSystem = GeneratedFoundationSuperSystem &
  BuildingEntityBase<"FOUNDATION_SUPER_SYSTEM">;
export type BuildingRailing = GeneratedRailing & BuildingEntityBase<"RAILING">;
export type BuildingRoofSystem = GeneratedRoofSystem & DocBuildingEntityBase<"ROOF_SYSTEM">;
export type BuildingRoom = Room & DocBuildingEntityBase<"ROOM">;
export type BuildingSite = GeneratedSite & DocBuildingEntityBase<"SITE">;
export type BuildingSkylight = SkylightUnit & BuildingEntityBase<"SKYLIGHT">;
export type BuildingStairSystem = GeneratedStairSystem & DocBuildingEntityBase<"STAIR_SYSTEM">;
export interface BuildingWallLayer extends Omit<BuildingEntityBase<"WALL_LAYER">, "docEntityHandle"> {
  wallLayerType: WallLayerType;
}
export interface BuildingWallPlaneSubdomain extends BuildingEntityBase<"WALL_PLANE_SUBDOMAIN">, WallPlaneSubdomain {
  readonly parent: BuildingWallPlaneHandle;
  readonly indexU: number;
  readonly indexV: number;
}
export type BuildingWindow = WindowUnit & BuildingEntityBase<"WINDOW">;
export type BuildingRoofVolume = GeneratedRoofVolume & BuildingEntityBase<"ROOF_VOLUME">;

export function makeBuildingEntity<T extends BuildingEntityType>(
  handle: BuildingEntityHandle,
  entity: any,
  docEntityHandle?: DocEntityHandle,
  entityInstanceType?: EntityInstanceType,
): Extract<BuildingEntity, { entityType: T }> {
  return castBuildingEntity<T>({
    ...entity,
    docEntityHandle: entity.docEntityHandle ?? docEntityHandle,
    entityInstanceType: entity.entityInstanceType ?? entityInstanceType,
    ...handle,
  });
}

export function isBuildingEntityOfType<T extends BuildingEntityType>(
  entity: BuildingEntity,
  type: T,
): entity is Extract<BuildingEntity, { entityType: T }> {
  return entity.entityType === type;
}

function castBuildingEntity<T extends BuildingEntityType>(
  entity: BuildingEntity,
): Extract<BuildingEntity, { entityType: T }> {
  return entity as Extract<BuildingEntity, { entityType: T }>;
}
